/* eslint-disable @ngrx/no-store-subscription */
/* eslint-disable @ngrx/avoid-mapping-selectors */
import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmButton } from '../../../../ui/button/src';
import { HlmLabel } from '@spartan-ng/helm/label';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { OrgRole, UserRole } from '@core/models/users';
import { i18n } from '@shared/pipes/i18n.pipe';
import { RemapRolePipe } from '@shared/pipes/remap-role.pipe';
import { Observable, map, of } from 'rxjs';
import { WhoAmIService } from 'src/app/auth/services/whoami.service';
import { environment } from 'src/environments/environment';

export interface InviteUserDialogContext {
  close: (result?: boolean) => void;
}

@Component({
  selector: 'app-user-invite-dialog',
  template: `
    <div class="p-6">
      <h1 class="text-center text-lg font-semibold mb-4">Invite user</h1>
      <form autocomplete="off" [formGroup]="inviteForm" class="row justify-content-center mb-4">
        <div class="col-12 pt-2 mb-3">
          <label hlmLabel>Email</label>
          <input hlmInput placeholder="Email of the user to invite" formControlName="email" required class="w-full" />
        </div>
        <div class="col-12 mb-3">
          <label hlmLabel>Role</label>
          <brn-select formControlName="role" class="w-full">
            <hlm-select-trigger>
              <hlm-select-value />
            </hlm-select-trigger>
            <hlm-select-content>
              @for (role of _roles | async; track role) {
                <hlm-option [value]="role">{{ role | remapRole | i18n }}</hlm-option>
              }
            </hlm-select-content>
          </brn-select>
        </div>
        @if (i18nEnabled) {
          <div class="col-12 mb-3">
            <label hlmLabel>Language</label>
            <brn-select formControlName="lang" class="w-full">
              <hlm-select-trigger>
                <hlm-select-value />
              </hlm-select-trigger>
              <hlm-select-content>
                <hlm-option value="it">Italian</hlm-option>
                <hlm-option value="en">English</hlm-option>
                <hlm-option value="pt">Portuguese</hlm-option>
              </hlm-select-content>
            </brn-select>
          </div>
        }
      </form>
      <div class="flex justify-center gap-3">
        <button hlmBtn variant="outline" (click)="onCancel()">Cancel</button>
        <button hlmBtn variant="outline" (click)="getUserInviteData()" [disabled]="!inviteForm.valid">
          Invite
        </button>
      </div>
    </div>
  `,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ...BrnSelectImports,
    ...HlmSelectImports,
    ...HlmInputImports,
    HlmLabel,
    HlmButton,
    AsyncPipe,
    RemapRolePipe,
    i18n,
  ],
})
export class InviteUserDialogComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private dr = inject(DestroyRef);
  private whoami = inject(WhoAmIService);
  private context = injectBrnDialogContext<InviteUserDialogContext>();

  inviteForm!: UntypedFormGroup;

  i18nEnabled = environment.ENABLE_I18N;

  _roles: Observable<OrgRole[]> = this.getRoles();

  isCurrentUserAdmin = this.whoami.isAdmin;

  isAdminRoleSelected = signal(false);

  shouldDisplayTenantSelection = computed(() => this.isCurrentUserAdmin() && !this.isAdminRoleSelected());

  constructor() {
    effect(() => (this.shouldDisplayTenantSelection() ? this.tenants?.enable() : this.tenants?.disable()));
  }

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      tenants: [null, [Validators.required]],
      lang: ['en', [Validators.required]],
      role: ['user', [Validators.required]],
    });

    this.inviteForm
      .get('role')!
      .valueChanges.pipe(
        takeUntilDestroyed(this.dr),
        map(role => role === UserRole.ADMIN)
      )
      .subscribe(ia => this.isAdminRoleSelected.set(ia));
  }

  getUserInviteData(): void {
    this.context.close(this.whoami.isAdmin());
  }

  onCancel(): void {
    this.context.close(false);
  }

  get role(): UntypedFormControl {
    return this.inviteForm.get('role') as UntypedFormControl;
  }

  get tenants(): UntypedFormControl {
    return this.inviteForm.get('tenants') as UntypedFormControl;
  }

  getRoles(): Observable<OrgRole[]> {
    return of([OrgRole.USER, OrgRole.OWNER]);
  }
}
